import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import Modal from 'react-bootstrap/Modal';
import CloseIcon from '@material-ui/icons/Close';
import Link from '@material-ui/core/Link';
import AccountService from '../../services/accountInfo';
import gqSupportService, { configureGQSupport } from '../../services/gqSupportService';
import './GQSupport.css';

// ---------------------------------------------------------------------------
// Design tokens — mirrors GenomeQuest's blue palette and typography
// ---------------------------------------------------------------------------
const t = {
  color: {
    primary:       'var(--gqs-primary)',
    primaryDark:   'var(--gqs-primary-dark)',
    primarySoft:   'var(--gqs-primary-soft)',
    text:          'var(--gqs-text)',
    textMuted:     'var(--gqs-text-muted)',
    textOnPrimary: '#FFFFFF',
    border:        'var(--gqs-border)',
    borderStrong:  'var(--gqs-border-strong)',
    bg:            'var(--gqs-bg)',
    bgSoft:        'var(--gqs-bg-soft)',
    bgHover:       'var(--gqs-bg-hover)',
    success:       'var(--gqs-success)',
    successBg:     'var(--gqs-success-bg)',
    successText:   'var(--gqs-success-text)',
    warning:       'var(--gqs-warning)',
    warningBg:     'var(--gqs-warning-bg)',
    warningText:   'var(--gqs-warning-text)',
    danger:        'var(--gqs-danger)',
    dangerBg:      'var(--gqs-danger-bg)',
    dangerText:    'var(--gqs-danger-text)',
    accent:        'var(--gqs-warning)',
    accentSoft:    'var(--gqs-warning-bg)',
    chatUserBg:    'var(--gqs-chat-user-bg)',
    chatAgentBg:   'var(--gqs-chat-agent-bg)',
    citationHi:    'var(--gqs-citation-hi)',
  },
  font: {
    family: 'Roboto, "Helvetica Neue", Helvetica, Arial, sans-serif',
    mono:   '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace',
    size: { xs: 11, sm: 12, md: 14, lg: 16, xl: 20, xxl: 24 },
  },
  radius: { sm: 4, md: 6, lg: 10, pill: 999 },
  shadow: {
    sm: '0 1px 2px rgba(0,0,0,0.06)',
    md: '0 4px 12px rgba(0,0,0,0.10)',
    lg: '0 8px 24px rgba(0,0,0,0.14)',
  },
};

// ---------------------------------------------------------------------------
// Tiny UI primitives
// ---------------------------------------------------------------------------
const Btn = ({ variant = 'primary', size = 'md', children, style, ...rest }) => {
  const pad = size === 'sm' ? '6px 12px' : '8px 16px';
  const fs  = size === 'sm' ? t.font.size.sm : t.font.size.md;
  const base = {
    border: 'none', borderRadius: t.radius.sm, cursor: 'pointer',
    fontFamily: t.font.family, fontSize: fs, fontWeight: 600,
    padding: pad, transition: 'background 0.15s',
  };
  const variants = {
    primary:   { background: t.color.primary,  color: t.color.textOnPrimary },
    secondary: { background: t.color.bg,       color: t.color.text, border: `1px solid ${t.color.borderStrong}` },
    ghost:     { background: 'transparent',    color: t.color.primary },
    danger:    { background: t.color.danger,   color: '#fff' },
  };
  return <button style={{ ...base, ...(variants[variant] || variants.primary), ...style }} {...rest}>{children}</button>;
};

const Badge = ({ tone = 'neutral', children }) => {
  const tones = {
    neutral: { bg: t.color.bgHover,   fg: t.color.text       },
    success: { bg: t.color.successBg, fg: t.color.successText },
    warning: { bg: t.color.warningBg, fg: t.color.warningText },
    danger:  { bg: t.color.dangerBg,  fg: t.color.dangerText  },
    info:    { bg: t.color.primarySoft, fg: t.color.primaryDark },
  };
  const tone_ = tones[tone] || tones.neutral;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px',
      borderRadius: t.radius.pill, background: tone_.bg, color: tone_.fg,
      fontSize: t.font.size.xs, fontWeight: 700, letterSpacing: 0.3,
    }}>{children}</span>
  );
};

// Minimal icon set
const Icon = ({ name, size = 16, color = 'currentColor' }) => {
  const paths = {
    search:   <path d="M11 4a7 7 0 105 12l4 4 1.5-1.5-4-4A7 7 0 0011 4zm0 2a5 5 0 110 10 5 5 0 010-10z" />,
    plus:     <path d="M11 5h2v6h6v2h-6v6h-2v-6H5v-2h6V5z" />,
    send:     <path d="M2 21l21-9L2 3v7l15 2-15 2v7z" />,
    copy:     <path d="M16 1H4a2 2 0 00-2 2v14h2V3h12V1zm3 4H8a2 2 0 00-2 2v14a2 2 0 002 2h11a2 2 0 002-2V7a2 2 0 00-2-2zm0 16H8V7h11v14z" />,
    refresh:  <path d="M17.65 6.35A8 8 0 1019.74 14h-2.06A6 6 0 1112 6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" />,
    upload:   <path d="M5 20h14v-2H5v2zm7-18l-5 5h3v6h4V7h3l-5-5z" />,
    file:     <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm4 18H6V4h7v5h5v11z" />,
    folder:   <path d="M10 4H4a2 2 0 00-2 2v12a2 2 0 002 2h16a2 2 0 002-2V8a2 2 0 00-2-2h-8l-2-2z" />,
    external: <path d="M14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7zM19 19H5V5h7V3H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7h-2v7z" />,
    close:    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />,
    trash:    <path d="M6 19a2 2 0 002 2h8a2 2 0 002-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />,
    check:    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />,
  };
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill={color} style={{ verticalAlign: 'middle' }}>
      {paths[name] || <circle cx="12" cy="12" r="3" />}
    </svg>
  );
};

// ---------------------------------------------------------------------------
// Sub-tab navigation
// ---------------------------------------------------------------------------
function SubTabs({ tabs, active, onChange, isAdmin, liveMode }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center',
      borderBottom: `2px solid ${t.color.border}`,
      background: t.color.bg, padding: '0 16px',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', flex: 1 }}>
        {tabs.filter(tab => !tab.adminOnly || isAdmin).map(tab => {
          const on = tab.id === active;
          return (
            <button key={tab.id} onClick={() => onChange(tab.id)} style={{
              padding: '10px 14px', border: 'none', cursor: 'pointer',
              background: 'transparent',
              color: on ? t.color.primary : t.color.textMuted,
              fontFamily: t.font.family, fontSize: t.font.size.md,
              fontWeight: on ? 600 : 400,
              borderBottom: on ? `2px solid ${t.color.primary}` : '2px solid transparent',
              marginBottom: -2,
              transition: 'color 0.15s, border-color 0.15s',
              whiteSpace: 'nowrap',
            }}>
              {tab.label}
              {tab.adminOnly && !on && <span style={{ marginLeft: 5, fontSize: 10, color: t.color.border }}>(admin)</span>}
            </button>
          );
        })}
      </div>
      <span style={{
        fontSize: t.font.size.xs, fontWeight: 600, padding: '3px 8px',
        borderRadius: t.radius.pill, marginBottom: 6,
        background: liveMode ? t.color.successBg : t.color.bgHover,
        color:      liveMode ? t.color.successText : t.color.textMuted,
      }}>
        {liveMode ? '● live' : '○ mock data'}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Screen: Ask (3-pane chat)
// ---------------------------------------------------------------------------
// Confirmation popup styled like FullText's other delete dialogs
// (react-bootstrap Modal + the shared titlebar / DMTBtn classes).
function ConfirmModal({ show, title = 'Delete', message, onConfirm, onCancel }) {
  return (
    <Modal id="modalCss" aria-labelledby="contained-modal-title-vcenter" centered show={!!show} onHide={onCancel}>
      <div>
        <div className="titlebar">
          <span>{title}</span>
          <Link href="#" onClick={(e) => e.preventDefault()} className="pull-right">
            <CloseIcon style={{ fill: '#b5b5b5' }} onClick={onCancel} />
          </Link>
        </div>
        <div style={{ borderTop: '1px solid #b5b5b5' }}>
          <p className="deletedialogtextteam">{message}</p>
        </div>
        <div style={{ float: 'right' }}>
          <button className="okButton DMTBtn" onClick={onConfirm}>OK</button>
          <button className="DMTBtn" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </Modal>
  );
}

// Notify the header (and anything else) that the pending-suggestions count changed,
// so the badge next to Preferences updates immediately instead of waiting for its poll.
function notifySuggestionsChanged() {
  try { window.dispatchEvent(new CustomEvent('gqSuggestionsChanged')); } catch (e) { /* no-op */ }
}

// Remove citation artefacts the agent appends to answers, so the saved Q&A is
// clean prose: the trailing "**Citations**" block, bare "[n] source" lines, and
// inline markers like [1], [2][3]. Mirrors the backend's stripCitations().
// Strip artefacts that shouldn't appear in the Q&A Library suggestion display:
//   1. "edit: ..." prefix — the raw user-edit reply stored verbatim as the answer
//   2. The verification block ("Does this look correct now? / Reply Yes…") appended
//      by the assistant after a verification round
// Extract the "**Question:** ..." line embedded by the agent in a text block.
// Returns it if found, otherwise null.
function extractEmbeddedQuestion(text) {
  if (!text) return null;
  // Match ONLY the "**Question:**" label line — must start a line and have the colon.
  // (A loose match also caught the word "question" mid-sentence, e.g.
  // "...federation question and confirm...", grabbing the wrong text.)
  const m = text.match(/^[ \t>*]*\*{0,2}Question\*{0,2}\s*:\s*\**\s*(.+)$/im);
  return m ? m[1].trim() : null;
}

// Decide what to show as the suggestion card title.
// Priority:
//   1. "**Question:** ..." embedded anywhere in the raw answer text
//   2. s.question — if it actually looks like a question (short + ends with ?)
//   3. s.question stripped of "edit:" — if it at least starts with a real sentence
//   4. Fallback: first sentence of the cleaned answer + "?"
function resolveSuggestionQuestion(s) {
  // 1. Extract from embedded **Question:** line anywhere in the raw answer
  const embedded = extractEmbeddedQuestion(s.answer);
  if (embedded) return embedded;

  const raw = (s.question || '').trim();
  // 2. Strip "edit :" / "edit:" prefix (with or without space before colon)
  const q = raw.replace(/^edit\s*:\s*/i, '').trim();
  // 3. Looks like a real question after stripping
  if (q.endsWith('?') && q.length < 200) return q;

  // 4. Fallback: pull the first sentence from the cleaned answer as a hint
  const cleanedAnswer = cleanSuggestionAnswer(s.answer || '');
  const firstSentence = cleanedAnswer.split(/(?<=[.!?])\s/)[0]?.trim();
  return firstSentence || 'Review this suggestion';
}

function cleanSuggestionAnswer(text) {
  if (!text) return '';
  let out = text;
  // Remove leading "edit :" / "edit:" prefix (backend stores it with a space sometimes)
  out = out.replace(/^edit\s*:\s*/i, '');
  // Remove everything from the "---" verification separator onward
  out = out.split(/\n---\n/)[0];
  // Remove "Does this look correct?" / "Does this answer look correct?" block + everything after
  out = out.replace(/does this (?:answer )?look correct[\s\S]*/i, '').trim();
  // Remove "Updated based on your changes:" header
  out = out.replace(/^updated based on your changes\s*:?\s*/i, '').trim();
  // Remove the embedded "**Question:**" label line (shown separately as the title).
  // Must be a real label line (starts a line, has the colon) so we don't chop the
  // body at the word "question" mid-sentence.
  out = out.replace(/^[ \t>*]*\*{0,2}Question\*{0,2}\s*:.*$/gim, '').trim();
  // Remove the trailing "Source(s):" citation block (singular or plural) — sources
  // are shown via the chips below, or inline in the answer already.
  out = out.replace(/^sources?\s*:[\s\S]*/im, '').trim();
  // Collapse excess blank lines left behind
  out = out.replace(/\n{3,}/g, '\n\n').trim();
  return out;
}

function stripCitations(text) {
  if (!text) return '';
  let out = text.replace(/\*\*citations\*\*[\s\S]*$/i, '').trim();   // drop Citations block + everything after
  out = out.replace(/^\[\d+\].*$/gim, '').trim();                    // drop leftover "[n] source" lines
  out = out.replace(/\[\d+\]/g, '').trim();                          // drop inline [n] markers
  out = out.replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n');   // tidy whitespace
  return out;
}

function AskScreen({ conversationLimit = null, readOnly = false, openConvId = null, onConvOpened }) {
  const [conversations, setConversations] = useState([]);
  const [activeId,      setActiveId]      = useState(null);
  const [activeConv,    setActiveConv]    = useState(null);
  const [draft,         setDraft]         = useState('');
  const draftMap = useRef({});   // persists unsent drafts per conversation id
  const [sending,       setSending]       = useState(false);
  const [previewCit,    setPreviewCit]    = useState(null);
  const [search,        setSearch]        = useState('');
  const [regenId,       setRegenId]       = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);   // convId awaiting delete confirm
  const [escalateMsg,   setEscalateMsg]   = useState(null);   // assistant message being escalated
  const [escToast,      setEscToast]      = useState(null);
  const [waitingForReply,    setWaitingForReply]    = useState(false);
  // Tracks the last agent message that included a verification block.
  // Shape: { messageId, conversationId, question, answer, citations }
  const [awaitingVerification, setAwaitingVerification] = useState(null);
  const threadRef    = useRef(null);
  const pollRef      = useRef(null);
  const textareaRef  = useRef(null);

  useEffect(() => {
    gqSupportService.listConversations().then(list => {
      setConversations(list);
      // If we were deep-linked from Analytics "Review →", open that conversation;
      // otherwise fall back to the most recent one that has messages.
      const target = openConvId ? list.find(c => c.id === openConvId) : null;
      const first = target || list.find(c => c.messages?.length);
      if (first) {
        setActiveId(first.id);
        setActiveConv(first);
      } else if (openConvId) {
        // Conversation isn't in the (possibly capped) list — fetch it directly.
        setActiveId(openConvId);
      }
      if (openConvId) onConvOpened?.();
    }).catch(err => {
      // Backend unreachable (Failed to fetch) — don't crash the page; show an
      // empty state with a toast so the user knows the service is down.
      setConversations([]);
      setEscToast(`Couldn't reach GQ Support: ${err.message}`);
      setTimeout(() => setEscToast(null), 5000);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!activeId) return;
    clearTimeout(pollRef.current);
    setWaitingForReply(false);
    // Restore this conversation's saved draft (empty string if none).
    setDraft(draftMap.current[activeId] || '');

    const startPolling = (id) => {
      const poll = () => {
        gqSupportService.getConversation(id).then(fresh => {
          if (!fresh) return;
          const msgs = fresh.messages || [];
          if (msgs.length && msgs[msgs.length - 1].role === 'assistant') {
            setActiveConv(fresh);
            setWaitingForReply(false);
          } else {
            pollRef.current = setTimeout(poll, 3000);
          }
        }).catch(() => { pollRef.current = setTimeout(poll, 3000); });
      };
      pollRef.current = setTimeout(poll, 3000);
    };

    gqSupportService.getConversation(activeId).then(c => {
      if (c) {
        setActiveConv(c);
        setPreviewCit(null);
        const msgs = c.messages || [];
        if (msgs.length && msgs[msgs.length - 1].role === 'user') {
          setWaitingForReply(true);
          startPolling(activeId);
        }
      }
    }).catch(() => { /* backend unreachable — leave the thread empty rather than crash */ });
    return () => clearTimeout(pollRef.current);
  }, [activeId]);

  useEffect(() => {
    if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
  }, [activeConv]);

  // Auto-resize the compose textarea to fit its content (grows as you type,
  // shrinks back when text is deleted). Cap at 200px so it doesn't eat the screen.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 200) + 'px';
  }, [draft]);

  // Detect verification block in the latest agent message.
  useEffect(() => {
    const msgs = activeConv?.messages || [];
    if (!msgs.length) return;
    const last = msgs[msgs.length - 1];
    if (last?.role !== 'assistant') return;
    if (!last.text?.includes('Does this answer look correct?') &&
        !last.text?.includes('Does this look correct')) return;
    // Prefer the "**Question:** ..." line embedded in the assistant message itself —
    // it's the original question and is always correct regardless of edit rounds.
    const embeddedQ = last.text.match(/\*{0,2}Question:{0,1}\*{0,2}\s*(.+)/i);
    let question = embeddedQ ? embeddedQ[1].trim() : '';
    // Fallback: scan user messages, skipping verification replies (Edit: / Yes / No).
    if (!question) {
      const isVerificationReply = t => /^(yes|yep|correct|right|looks good|edit\s*:|no\b)/i.test(t?.trim() || '');
      question = [...msgs].reverse().find(
        m => m.role === 'user' && !isVerificationReply(m.text)
      )?.text || '';
    }
    // Strip the verification block (everything before "---") and the citations
    // (the "**Citations**" block + inline [n] markers) so the saved answer is clean.
    const answer = stripCitations(last.text.split(/\n---\n/)[0].trim());
    setAwaitingVerification({
      messageId:      last.id,
      conversationId: activeConv.id,
      question,
      answer,
      citations: last.citations || [],
    });
  }, [activeConv]);

  const onNewConversation = () => {
    // Don't persist an empty conversation row here. Just reset to the blank
    // "How can I help?" compose state; the backend row is created lazily on the
    // first send (see onSend, which handles activeConv === null). Creating a row
    // on every click made empty "New conversation · 0 messages" placeholders
    // pile up in the sidebar whenever the user opened an old chat and clicked
    // "New conversation" again.
    clearTimeout(pollRef.current);
    setWaitingForReply(false);
    setActiveId(null);
    setActiveConv(null);
    setPreviewCit(null);
    setAwaitingVerification(null);
    setDraft('');
  };

  const onDeleteConversation = (convId, e) => {
    e?.stopPropagation();      // don't select the row when clicking the × button
    setPendingDelete(convId);  // open the confirm popup
  };
  const confirmDelete = async () => {
    const convId = pendingDelete;
    setPendingDelete(null);
    if (!convId) return;
    // Optimistic delete: remove the row immediately so the UI feels instant,
    // then call the backend. If it fails, restore the row and surface the error.
    const prevConversations = conversations;
    const wasActive = activeId === convId;
    setConversations(prev => prev.filter(c => c.id !== convId));
    if (wasActive) { setActiveId(null); setActiveConv(null); setPreviewCit(null); }
    try {
      await gqSupportService.deleteConversation(convId);
      setEscToast('Conversation deleted.');
    } catch (err) {
      // Roll back — put the conversation back where it was.
      setConversations(prevConversations);
      if (wasActive) setActiveId(convId);
      setEscToast(`Couldn't delete this conversation: ${err.message}`);
    }
    setTimeout(() => setEscToast(null), 3500);
  };

  // Detect if the user's text is a verification reply (Yes / Edit: ... / No ...).
  // Returns { action, edits } or null.
  const detectVerificationReply = (text) => {
    const t = text.trim();
    if (/^(yes|yep|correct|right|looks good|that'?s right)\b/i.test(t))
      return { action: 'yes', edits: null };
    if (/^edit\s*:/i.test(t))
      return { action: 'edit', edits: t.replace(/^edit\s*:\s*/i, '').trim() };
    if (/^no\s*[,.]?\s*(the answer is|actually|it'?s)\s*/i.test(t))
      return { action: 'no', edits: t.replace(/^no\s*[,.]?\s*(the answer is|actually|it'?s)\s*/i, '').trim() };
    return null;
  };

  const onSend = async () => {
    const text = draft.trim();
    if (!text || sending) return;
    clearTimeout(pollRef.current);
    setWaitingForReply(false);
    setSending(true);
    setDraft('');
    delete draftMap.current[activeId];  // message sent — discard the saved draft

    // Check for verification reply BEFORE sending to chat.
    let verificationAction = null;   // 'yes' | 'edit' | 'no' for this turn
    let prevVerifiedMsgId  = null;   // the answer being verified (to move the submission to the new one)
    if (awaitingVerification) {
      const vReply = detectVerificationReply(text);
      if (vReply) {
        verificationAction = vReply.action;
        prevVerifiedMsgId  = awaitingVerification.messageId;
        if (vReply.action === 'yes') {
          // "Yes" = confirm as-is → submit the answer for admin approval (it lands in
          // the Suggestions tab, same as the "Send for approval" button). An admin
          // reviews and promotes it into the Approved library — it is NOT auto-approved.
          const vConvId = awaitingVerification.conversationId;
          const vMsgId  = awaitingVerification.messageId;
          gqSupportService.submitForApproval(vConvId, vMsgId, true)
            .then(() => {
              notifySuggestionsChanged();
              setEscToast('Answer sent to Suggestions for review.');
              setTimeout(() => setEscToast(null), 4000);
            })
            .catch(() => {
              setEscToast('Could not send for approval — check the Q&A Library tab.');
              setTimeout(() => setEscToast(null), 4000);
            });
          setActiveConv(prev => ({
            ...prev,
            messages: prev.messages.map(m => m.id === vMsgId ? { ...m, submittedForApproval: true } : m),
          }));
        }
        // For 'edit' / 'no' we do NOT auto-approve — the regenerated answer below is
        // submitted for approval (handled after the reply lands) so an admin reviews it.
        setAwaitingVerification(null);
      }
    }

    // On first open there's no active conversation yet (the "How can I help?"
    // screen). Create one on the fly so the user can just type and send without
    // first clicking "New conversation".
    let conv = activeConv;
    const created = !conv;
    if (!conv) {
      try {
        conv = await gqSupportService.createConversation();
      } catch (err) {
        setSending(false);
        setEscToast(`Couldn't start a conversation: ${err.message}`);
        setTimeout(() => setEscToast(null), 3500);
        return;
      }
      setConversations(prev => [conv, ...prev]);
      setActiveConv(conv);
      // NB: do NOT setActiveId here. That would fire the [activeId] refetch
      // effect against the still-empty backend conversation and wipe the
      // optimistic user question. We set it after the reply lands (below).
    }

    const convId  = conv.id;
    const isFirst = !conv.messages?.length;   // first turn → becomes the title
    const userMsg = { id: 'u' + Date.now(), role: 'user', text };
    setActiveConv(prev => {
      const base = (prev && prev.id === convId) ? prev : conv;
      return { ...base, messages: [...(base.messages || []), userMsg] };
    });

    let reply;
    try {
      reply = await gqSupportService.sendMessage(convId, text);
    } catch (err) {
      setSending(false);
      setEscToast(`Couldn't send your message: ${err.message}`);
      setTimeout(() => setEscToast(null), 3500);
      return;
    }
    setActiveConv(prev => ({ ...prev, messages: [...prev.messages, reply] }));
    setSending(false);
    // Select the new conversation now that its turns are persisted, so the
    // sidebar highlights it (and any refetch returns the full question+answer).
    if (created) setActiveId(convId);

    // After an Edit / No, submit the regenerated answer for admin approval (instead
    // of auto-approving). Withdraw the previous round's submission first so repeated
    // edits don't stack multiple suggestions for the same question.
    if ((verificationAction === 'edit' || verificationAction === 'no') && reply?.id) {
      try {
        if (prevVerifiedMsgId) {
          await gqSupportService.submitForApproval(convId, prevVerifiedMsgId, false);
          setActiveConv(prev => ({
            ...prev,
            messages: prev.messages.map(m => m.id === prevVerifiedMsgId ? { ...m, submittedForApproval: false } : m),
          }));
        }
        await gqSupportService.submitForApproval(convId, reply.id, true);
        setActiveConv(prev => ({
          ...prev,
          messages: prev.messages.map(m => m.id === reply.id ? { ...m, submittedForApproval: true } : m),
        }));
        notifySuggestionsChanged();
        setEscToast('Edited answer sent to Suggestions for review.');
        setTimeout(() => setEscToast(null), 4000);
      } catch (e) { /* non-fatal — they can still use Send for approval */ }
    }

    // Keep the history sidebar in sync with the thread: the first question
    // becomes the title (matching the backend), and the message count + time
    // update each turn. Without this the list entry stays "New conversation · 0 messages".
    setConversations(prev => prev.map(c =>
      c.id === convId
        ? {
            ...c,
            title:     isFirst ? text.slice(0, 60) : c.title,
            updatedAt: new Date().toISOString(),
            messages:  [...(c.messages || []), userMsg, reply],
          }
        : c
    ));
  };

  const onRegenerate = async (msgId) => {
    if (!activeConv || regenId) return;
    setRegenId(msgId);
    try {
      const fresh = await gqSupportService.regenerateMessage(activeConv.id, msgId);
      setActiveConv(prev => ({ ...prev, messages: [...prev.messages, fresh] }));   // append below
    } finally {
      setRegenId(null);
    }
  };

  // "Send for approval" toggle — submits an answer to the Q&A Suggestions review
  // queue (or withdraws it). No thumbs rating involved.
  const onSubmitApproval = async (msgId, submitted) => {
    if (!activeConv) return;
    try {
      await gqSupportService.submitForApproval(activeConv.id, msgId, submitted);
      setActiveConv(prev => ({
        ...prev,
        messages: prev.messages.map(m => m.id === msgId ? { ...m, submittedForApproval: submitted } : m),
      }));
      notifySuggestionsChanged();
    } catch (e) {
      console.error('Submit for approval failed:', e);
    }
  };

  const onSubmitEscalation = async ({ note, priority }) => {
    if (!escalateMsg || !activeConv) return;
    try {
      const res = await gqSupportService.createEscalation({
        convId: activeConv.id, messageId: escalateMsg.id, note, priority,
      });
      setEscalateMsg(null);
      setEscToast(res?.emailSent === false
        ? 'Escalation recorded (email not sent — check SendGrid config).'
        : 'Escalated to L2. The GQ support team has been emailed.');
    } catch (err) {
      setEscToast(`Escalation failed: ${err.message}`);
    }
    setTimeout(() => setEscToast(null), 3500);
  };

  const filteredConvs = useMemo(() => {
    if (!search) return conversations;
    const s = search.toLowerCase();
    return conversations.filter(c => (c.title || '').toLowerCase().includes(s));
  }, [conversations, search]);

  const grouped = useMemo(() => {
    // History (read-only) must not list empty "New conversation" placeholders the
    // user opened in Ask but never sent a message in — only real conversations.
    let base = readOnly ? filteredConvs.filter(c => (c.messages?.length || 0) > 0) : filteredConvs;
    // Ask tab caps at the latest N (e.g. 10); History tab passes null → all.
    base = conversationLimit ? base.slice(0, conversationLimit) : base;
    return {
      today:     base.filter(c => c.bucket === 'today'),
      yesterday: base.filter(c => c.bucket === 'yesterday'),
      last7:     base.filter(c => c.bucket === 'last7'),
      older:     base.filter(c => !['today', 'yesterday', 'last7'].includes(c.bucket)),
    };
  }, [filteredConvs, conversationLimit, readOnly]);

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 160px)', minHeight: 500 }}>
      {/* LEFT: conversation history */}
      <aside style={{
        width: 275, background: t.color.bgSoft, borderRight: `1px solid ${t.color.border}`,
        padding: '14px 12px', display: 'flex', flexDirection: 'column', gap: 8, overflowY: 'auto',
      }}>
        {!readOnly && (
          <Btn onClick={onNewConversation} style={{ width: '100%' }}>
            <Icon name="plus" size={14} color="#fff" /> &nbsp; New conversation
          </Btn>
        )}
        <div style={{ position: 'relative' }}>
          <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', display: 'flex', alignItems: 'center' }}>
            <Icon name="search" size={14} color={t.color.textMuted} />
          </span>
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search conversations"
            style={{
              width: '100%', padding: '7px 10px 7px 30px', boxSizing: 'border-box',
              border: `1px solid ${t.color.borderStrong}`, borderRadius: t.radius.sm,
              fontFamily: t.font.family, fontSize: t.font.size.sm,
              background: t.color.bg, color: t.color.text,
            }}
          />
        </div>
        <HistoryGroup label="TODAY"        items={grouped.today}     activeId={activeId} onPick={setActiveId} onDelete={onDeleteConversation} />
        <HistoryGroup label="YESTERDAY"    items={grouped.yesterday} activeId={activeId} onPick={setActiveId} onDelete={onDeleteConversation} />
        <HistoryGroup label="LAST 7 DAYS"  items={grouped.last7}     activeId={activeId} onPick={setActiveId} onDelete={onDeleteConversation} />
        <HistoryGroup label="OLDER"        items={grouped.older}     activeId={activeId} onPick={setActiveId} onDelete={onDeleteConversation} />
        {search.trim() &&
          !grouped.today.length && !grouped.yesterday.length &&
          !grouped.last7.length && !grouped.older.length && (
            <div style={{ color: t.color.textMuted, fontSize: t.font.size.sm, textAlign: 'center', padding: '16px 8px' }}>
              No Matches Found
            </div>
          )}
        <ConfirmModal
          show={!!pendingDelete}
          title="Delete"
          message="Are you sure you want to delete this conversation?"
          onConfirm={confirmDelete}
          onCancel={() => setPendingDelete(null)}
        />
      </aside>

      {/* CENTER: chat */}
      <main style={{ flex: 1, display: 'flex', flexDirection: 'column', background: t.color.bg, minWidth: 0 }}>
        <div ref={threadRef} style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
          {!activeConv?.messages?.length && (readOnly
            ? <div style={{ maxWidth: 560, margin: '60px auto', textAlign: 'center', color: t.color.textMuted }}>
                Select a conversation to view it. New conversations are started from the <strong>Ask</strong> tab.
              </div>
            : <EmptyChat onPrompt={txt => setDraft(txt)} />)}
          {activeConv?.messages?.map(m => (
            <ChatMessage key={m.id} m={m} onSubmitApproval={onSubmitApproval}
              onRegenerate={readOnly ? undefined : onRegenerate} regenerating={regenId === m.id}
              onEscalate={m.role === 'assistant' ? setEscalateMsg : undefined} />
          ))}
          {(sending || waitingForReply) && <TypingDots />}
        </div>
        {!readOnly && (
        <div style={{ padding: '12px 16px', borderTop: `1px solid ${t.color.border}`, background: t.color.bg }}>
          <div style={{
            display: 'flex', alignItems: 'flex-end', gap: 8,
            border: `1px solid ${t.color.borderStrong}`, borderRadius: t.radius.lg,
            padding: '8px 8px 8px 14px', background: t.color.bg,
            boxShadow: t.shadow.sm,
          }}>
            <textarea
              ref={textareaRef}
              value={draft}
              onChange={e => { setDraft(e.target.value); draftMap.current[activeId] = e.target.value; }}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSend(); } }}
              placeholder="Ask GQ Support… (Shift+Enter for newline)"
              rows={1}
              style={{
                flex: 1, border: 'none', outline: 'none', resize: 'none',
                fontFamily: t.font.family, fontSize: t.font.size.md,
                padding: '6px 4px', minHeight: 36, maxHeight: 200,
                overflowY: 'auto', lineHeight: '1.5',
                background: 'transparent', color: t.color.text,
              }}
            />
            <Btn onClick={onSend} disabled={sending || !draft.trim()} style={{ padding: '6px 12px' }}>
              <Icon name="send" size={14} color="#fff" />
            </Btn>
          </div>
          <div style={{ fontSize: t.font.size.xs, color: t.color.textMuted, marginTop: 6 }}>
            Answers are grounded in GQ documentation. Always click a citation to verify.
          </div>
        </div>
        )}
      </main>

      {/* RIGHT: source preview */}
      <aside style={{
        width: previewCit ? 400 : 0, transition: 'width 0.2s', overflow: 'hidden',
        background: t.color.bgSoft, borderLeft: `1px solid ${t.color.border}`,
      }}>
        {previewCit && <SourcePreview citation={previewCit} onClose={() => setPreviewCit(null)} />}
      </aside>

      <EscalateModal
        message={escalateMsg}
        onSubmit={onSubmitEscalation}
        onCancel={() => setEscalateMsg(null)}
      />
      {escToast && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          background: t.color.text, color: '#fff', padding: '10px 18px',
          borderRadius: t.radius.sm, fontSize: t.font.size.sm, zIndex: 2000, boxShadow: t.shadow.sm,
        }}>{escToast}</div>
      )}
    </div>
  );
}

// Modal shown when escalating an assistant answer to L2. The question/answer
// are snapshotted server-side from the message id; here we collect a priority
// and an optional note for the support team.
function EscalateModal({ message, onSubmit, onCancel }) {
  const [note, setNote]         = useState('');
  const [priority, setPriority] = useState('normal');
  const [busy, setBusy]         = useState(false);

  useEffect(() => { if (message) { setNote(''); setPriority('normal'); setBusy(false); } }, [message]);

  if (!message) return null;
  const submit = async () => { setBusy(true); await onSubmit({ note, priority }); setBusy(false); };

  return (
    <Modal id="modalCss" centered show={!!message} onHide={onCancel}>
      <div>
        <div className="titlebar">
          <span>Escalate to L2</span>
          <Link href="#" onClick={(e) => e.preventDefault()} className="pull-right">
            <CloseIcon style={{ fill: '#b5b5b5' }} onClick={onCancel} />
          </Link>
        </div>
        <div style={{ borderTop: '1px solid #b5b5b5', padding: '14px 16px' }}>
          <p style={{ color: t.color.textMuted, fontSize: t.font.size.sm, marginTop: 0 }}>
            This sends the question, the agent's answer, and its citations to the GQ support team
            (<strong>GQ-Support@aptean.com</strong>) and adds it to the Escalation queue.
          </p>

          <label style={{ fontSize: t.font.size.sm, fontWeight: 600, color: t.color.text }}>Priority</label>
          <select
            value={priority}
            onChange={e => setPriority(e.target.value)}
            style={{
              display: 'block', width: '100%', margin: '6px 0 14px', padding: '7px 10px',
              border: `1px solid ${t.color.borderStrong}`, borderRadius: t.radius.sm,
              fontFamily: t.font.family, fontSize: t.font.size.sm, boxSizing: 'border-box',
              background: t.color.bg, color: t.color.text,
            }}>
            <option value="low"    style={{ background: t.color.bg, color: t.color.text }}>Low</option>
            <option value="normal" style={{ background: t.color.bg, color: t.color.text }}>Normal</option>
            <option value="high"   style={{ background: t.color.bg, color: t.color.text }}>High</option>
            <option value="urgent" style={{ background: t.color.bg, color: t.color.text }}>Urgent</option>
          </select>

          <label style={{ fontSize: t.font.size.sm, fontWeight: 600, color: t.color.text }}>
            Note for L2 <span style={{ color: t.color.textMuted, fontWeight: 400 }}>(optional)</span>
          </label>
          <textarea
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="What did the customer try? Anything the answer missed?"
            rows={4}
            style={{
              display: 'block', width: '100%', margin: '6px 0 4px', padding: '8px 10px',
              border: `1px solid ${t.color.borderStrong}`, borderRadius: t.radius.sm,
              fontFamily: t.font.family, fontSize: t.font.size.sm, boxSizing: 'border-box', resize: 'vertical',
            }}
          />
        </div>
        <div style={{ float: 'right', padding: '0 12px 12px' }}>
          <button className="okButton DMTBtn" disabled={busy} onClick={submit}>
            {busy ? 'Sending…' : 'Escalate'}
          </button>
          <button className="DMTBtn" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </Modal>
  );
}

function HistoryGroup({ label, items, activeId, onPick, onDelete }) {
  if (!items?.length) return null;
  return (
    <div>
      <div style={{ fontSize: t.font.size.xs, fontWeight: 700, color: t.color.textMuted, padding: '8px 4px 4px', letterSpacing: 0.5 }}>
        {label}
      </div>
      {items.map(c => {
        const on = c.id === activeId;
        return (
          <div key={c.id} onClick={() => onPick(c.id)} style={{
            padding: '8px 10px', borderRadius: t.radius.sm, cursor: 'pointer',
            background: on ? t.color.primarySoft : t.color.bg,
            marginBottom: 6, display: 'flex', alignItems: 'flex-start', gap: 6,
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, color: t.color.text, fontSize: t.font.size.sm }}>{c.title}</div>
              <div style={{ color: t.color.textMuted, fontSize: t.font.size.xs, marginTop: 2 }}>
                {new Date(c.updatedAt).toLocaleString()} · {c.messages?.length || 0} messages
              </div>
            </div>
            {onDelete && (
              <button onClick={(e) => onDelete(c.id, e)} title="Delete conversation"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: t.color.textMuted, padding: 2, lineHeight: 0, flexShrink: 0 }}>
                <Icon name="trash" size={15} />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

function EmptyChat({ onPrompt }) {
  const suggestions = [
    'Why should I use Genepast or blast?',
    'Can I search the full text in my sequence search results?',
    'How often are the patent databases updated?',
    'Can I retrieve and visualize all the patent sequences matching 2 or more of my queries?',
  ];
  return (
    <div style={{ maxWidth: 560, margin: '60px auto', textAlign: 'center' }}>
      <div style={{ fontSize: 26, fontWeight: 700, color: t.color.primary, marginBottom: 8 }}>How can I help?</div>
      <div style={{ color: t.color.textMuted, marginBottom: 24 }}>
        Ask anything about GenomeQuest — I'll answer from the official docs and link the sources.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        {suggestions.map(s => (
          <div key={s} onClick={() => onPrompt(s)} style={{
            padding: '12px 14px', textAlign: 'left',
            border: `1px solid ${t.color.border}`, borderRadius: t.radius.md,
            cursor: 'pointer', background: t.color.bg, color: t.color.text,
            fontSize: t.font.size.sm, transition: 'background 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = t.color.bgSoft; }}
          onMouseLeave={e => { e.currentTarget.style.background = t.color.bg; }}
          >{s}</div>
        ))}
      </div>
    </div>
  );
}

// Minimal markdown: turn line-leading "- "/"* " into • bullets, then render
// **bold** as <strong>. Newlines are preserved by the parent's pre-wrap.
function renderBold(s, key) {
  return s.split(/\*\*(.+?)\*\*/g).map((part, i) =>
    i % 2 === 1 ? <strong key={`${key}-b${i}`}>{part}</strong> : part
  );
}

function renderRich(text) {
  const lines = String(text ?? '').split('\n');
  return lines.map((line, i) => {
    const bullet = line.match(/^[ \t]*[-*][ \t]+(.*)$/);
    if (bullet) {
      return (
        <div key={`ln${i}`} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, margin: '3px 0' }}>
          <span style={{ flexShrink: 0, width: '1em', textAlign: 'center', fontSize: '1.8em', lineHeight: 1.05 }}>•</span>
          <span style={{ flex: 1, minWidth: 0 }}>{renderBold(bullet[1], `ln${i}`)}</span>
        </div>
      );
    }
    if (line.trim() === '') return <div key={`ln${i}`} style={{ height: '0.5em' }} />;
    return <div key={`ln${i}`} style={{ margin: '2px 0' }}>{renderBold(line, `ln${i}`)}</div>;
  });
}

function ChatMessage({ m, onCitation, onSubmitApproval, onRegenerate, regenerating, onEscalate }) {
  const [approvalHovered, setApprovalHovered] = React.useState(false);
  if (m.role === 'user') {
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <div style={{
          background: t.color.chatUserBg, color: '#fff',
          padding: '10px 14px', borderRadius: 14, maxWidth: '70%',
          fontSize: t.font.size.md, lineHeight: 1.5, whiteSpace: 'pre-wrap',
        }}>{m.text}</div>
      </div>
    );
  }
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ fontSize: t.font.size.xs, color: t.color.textMuted, fontWeight: 600, marginBottom: 6 }}>
        GQ Support Agent
      </div>
      <div style={{
        background: t.color.chatAgentBg, padding: '12px 16px', borderRadius: 14, maxWidth: '80%',
        border: `1px solid ${t.color.border}`,   // outlined like GQ Search's agent bubble
        fontSize: t.font.size.md, lineHeight: 1.55, whiteSpace: 'pre-wrap',
      }}>{renderRich(m.text)}</div>
      {/* Citation chips removed — the GQ Support flow embeds citations inline in
          the answer text (the "Citations" block), and the structured citations[]
          returns no usable source content for the preview panel. */}
      <div style={{ display: 'flex', gap: 12, marginTop: 10, color: t.color.textMuted, fontSize: t.font.size.sm, alignItems: 'center' }}>
        <MsgAction icon="copy"    onClick={() => navigator.clipboard?.writeText(m.text)} label="Copy" />
        {onRegenerate && (
          <MsgAction icon="refresh" label={regenerating ? 'Regenerating…' : 'Regenerate'} onClick={() => onRegenerate(m.id)} />
        )}
        {onSubmitApproval && (() => {
          const sent = !!m.submittedForApproval;
          const showUndo = sent && approvalHovered;
          return (
            <button
              onClick={() => onSubmitApproval(m.id, !sent)}
              onMouseEnter={() => setApprovalHovered(true)}
              onMouseLeave={() => setApprovalHovered(false)}
              title={sent ? 'Click to undo — remove from approval queue' : 'Send this answer to Q&A Library for admin approval'}
              style={{
                display: 'inline-flex', alignItems: 'center',
                padding: '3px 12px', borderRadius: 20,
                border: `1px solid ${showUndo ? t.color.danger : sent ? t.color.success : t.color.border}`,
                background: showUndo ? t.color.danger : sent ? t.color.success : 'transparent',
                color: sent ? '#fff' : t.color.success,
                fontFamily: t.font.family, fontSize: t.font.size.sm,
                fontWeight: 600, cursor: 'pointer',
                transition: 'all 0.15s',
              }}>
              {showUndo ? 'Undo approval' : sent ? 'Sent for approval' : 'Send for approval'}
            </button>
          );
        })()}
        {/* Escalate to L2 ticket — disabled for now
        {onEscalate && (
          <button
            onClick={() => onEscalate(m)}
            style={{ background: 'none', border: 'none', color: t.color.primary, fontFamily: t.font.family, fontSize: t.font.size.sm, cursor: 'pointer', padding: 0 }}>
            Escalate to L2 ticket →
          </button>
        )}
        */}
      </div>
    </div>
  );
}

function MsgAction({ icon, active, activeColor, onClick, label }) {
  return (
    <button onClick={onClick} title={label} style={{
      background: 'none', border: 'none', cursor: 'pointer',
      color: active ? activeColor : t.color.textMuted,
      display: 'flex', alignItems: 'center', gap: 4,
      fontFamily: t.font.family, fontSize: t.font.size.sm, padding: 0,
    }}>
      <Icon name={icon} size={14} />
      <span>{label}</span>
    </button>
  );
}

function TypingDots() {
  return (
    <div style={{ display: 'flex', gap: 4, padding: '6px 16px' }}>
      {[0, 1, 2].map(i => (
        <span key={i} className={`gqs-dot gqs-dot-${i}`} style={{
          width: 6, height: 6, borderRadius: '50%', background: t.color.textMuted,
        }} />
      ))}
    </div>
  );
}

function SourcePreview({ citation, onClose }) {
  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '12px 16px', borderBottom: `1px solid ${t.color.border}`, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: t.font.size.xs, fontWeight: 700, color: t.color.textMuted, letterSpacing: 0.5 }}>SOURCE PREVIEW</div>
          <div style={{ fontSize: t.font.size.sm, color: t.color.text, marginTop: 4 }}>
            docs / support / <strong>{citation.file}</strong>
          </div>
          <div style={{ fontSize: t.font.size.xs, color: t.color.textMuted }}>Section: {citation.section}</div>
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: t.color.textMuted }}>
          <Icon name="close" size={16} />
        </button>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: 18, fontSize: t.font.size.md, lineHeight: 1.6 }}>
        <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 10 }}>
          # {citation.file.replace(/\.md$/, '').replace(/-/g, ' ')}
        </div>
        <div style={{ fontWeight: 700, fontSize: 15, margin: '16px 0 6px' }}>## {citation.section}</div>
        <div style={{
          background: t.color.citationHi, border: `1px solid ${t.color.warning}`,
          padding: 12, borderRadius: t.radius.md, color: t.color.text, fontWeight: 500,
        }}>{citation.excerpt}</div>
        <p style={{ color: t.color.textMuted, marginTop: 16, fontSize: t.font.size.sm }}>
          (Surrounding paragraphs render here from the full .md source.)
        </p>
      </div>
      <div style={{ padding: 14, borderTop: `1px solid ${t.color.border}` }}>
        <Btn variant="secondary" style={{ width: '100%' }}>
          <Icon name="external" size={14} /> &nbsp; Open original .md
        </Btn>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Screen: Source Library
// ---------------------------------------------------------------------------
function SourceLibraryScreen() {
  const [sources, setSources] = useState([]);
  const [folders, setFolders] = useState([]);
  const [folder,  setFolder]  = useState('All sources');
  const [status,  setStatus]  = useState('All');
  const [search,  setSearch]  = useState('');
  const [busyId,  setBusyId]  = useState(null);

  useEffect(() => {
    gqSupportService.listSources().then(setSources);
    gqSupportService.listFolders().then(setFolders);
  }, []);

  const filtered = sources.filter(s =>
    (folder === 'All sources' || s.folder === folder) &&
    (status === 'All' || s.status === status.toLowerCase()) &&
    (!search || s.name.toLowerCase().includes(search.toLowerCase()))
  );

  const onReindex = async (id) => {
    setBusyId(id);
    try {
      await gqSupportService.reindexSource(id);
      setSources(prev => prev.map(s => s.id === id ? { ...s, status: 'indexed', lastIndexed: 'just now' } : s));
    } finally {
      setBusyId(null);
    }
  };

  const onDelete = async (id) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Remove this source from the index?')) return;
    // Optimistic: drop the row immediately, restore it if the backend fails.
    const prevSources = sources;
    setSources(prev => prev.filter(s => s.id !== id));
    try {
      await gqSupportService.deleteSource(id);
    } catch (err) {
      setSources(prevSources);
      // eslint-disable-next-line no-alert
      alert(`Couldn't delete this source: ${err.message}`);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16 }}>
        <SearchInput value={search} onChange={setSearch} placeholder="Search by filename…" width={300} />
        <GQSelect value={folder} onChange={setFolder} options={['All sources', ...folders.map(f => f.name).filter(n => n !== 'All sources')]} />
        <GQSelect value={status} onChange={setStatus} options={['All', 'Indexed', 'Pending', 'Failed']} />
        <div style={{ flex: 1 }} />
        <Btn>+ Upload .md files</Btn>
      </div>
      <div style={{ display: 'flex', gap: 16 }}>
        {/* Folder tree */}
        <aside style={{ width: 240, background: t.color.bgSoft, border: `1px solid ${t.color.border}`, borderRadius: t.radius.md, padding: 12 }}>
          <div style={{ fontSize: t.font.size.xs, fontWeight: 700, color: t.color.textMuted, letterSpacing: 0.5, marginBottom: 8 }}>FOLDERS</div>
          {folders.map(f => {
            const on = folder === f.name;
            return (
              <div key={f.name} onClick={() => setFolder(f.name)} style={{
                padding: '6px 10px', borderRadius: t.radius.sm, cursor: 'pointer',
                background: on ? t.color.primarySoft : 'transparent',
                color: on ? t.color.primaryDark : t.color.text,
                fontWeight: on ? 600 : 400, fontSize: t.font.size.sm,
                display: 'flex', justifyContent: 'space-between',
              }}>
                <span><Icon name="folder" size={13} color={on ? t.color.primary : t.color.textMuted} /> &nbsp; {f.name}</span>
                <span style={{ color: t.color.textMuted }}>({f.count})</span>
              </div>
            );
          })}
          <div style={{ marginTop: 18, fontSize: t.font.size.xs, fontWeight: 700, color: t.color.textMuted, letterSpacing: 0.5 }}>CONNECTED SOURCES</div>
          <ConnectorRow label="docs/" sub="GitHub · synced 12 min ago" />
          <ConnectorRow label="Support KB" sub="Confluence · synced 2h ago" />
        </aside>

        {/* Table */}
        <div style={{ flex: 1, border: `1px solid ${t.color.border}`, borderRadius: t.radius.md, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: t.font.family }}>
            <thead>
              <tr style={{ background: t.color.primary, color: '#fff' }}>
                {['Name', 'Folder', 'Size', 'Last indexed', 'Status', '# Citations', 'Actions'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '10px 12px', fontSize: t.font.size.sm, fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((s, i) => (
                <tr key={s.id} style={{ background: i % 2 ? t.color.bgSoft : t.color.bg, borderTop: `1px solid ${t.color.border}` }}>
                  <td style={cellSt}><Icon name="file" size={14} color={t.color.textMuted} /> &nbsp; {s.name}</td>
                  <td style={cellSt}>{s.folder}</td>
                  <td style={cellSt}>{s.sizeKb} KB</td>
                  <td style={cellSt}>{s.lastIndexed}</td>
                  <td style={cellSt}>
                    {s.status === 'indexed' && <Badge tone="success">Indexed</Badge>}
                    {s.status === 'pending' && <Badge tone="warning">Pending</Badge>}
                    {s.status === 'failed'  && <Badge tone="danger">Failed</Badge>}
                  </td>
                  <td style={cellSt}>{s.citations || '—'}</td>
                  <td style={{ ...cellSt, color: t.color.primary }}>
                    <IconAction icon="refresh" title="Re-index" onClick={() => onReindex(s.id)} loading={busyId === s.id} />
                    <IconAction icon="file"    title="View" />
                    <IconAction icon="close"   title="Delete" onClick={() => onDelete(s.id)} />
                  </td>
                </tr>
              ))}
              {!filtered.length && (
                <tr><td colSpan={7} style={{ padding: 24, textAlign: 'center', color: t.color.textMuted }}>
                  No sources match your filters.
                </td></tr>
              )}
            </tbody>
          </table>
          <div style={{ padding: '10px 14px', display: 'flex', justifyContent: 'space-between', color: t.color.textMuted, fontSize: t.font.size.sm, borderTop: `1px solid ${t.color.border}` }}>
            <span>Showing {filtered.length} of {sources.length}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

const cellSt = { padding: '10px 12px', fontSize: t.font.size.sm, color: t.color.text };

function IconAction({ icon, title, onClick, loading }) {
  return (
    <button onClick={onClick} title={title} disabled={loading} style={{
      background: 'none', border: 'none', cursor: loading ? 'wait' : 'pointer',
      color: t.color.primary, padding: '4px 6px', opacity: loading ? 0.5 : 1,
    }}>
      <Icon name={icon} size={15} />
    </button>
  );
}

function ConnectorRow({ label, sub }) {
  return (
    <div style={{ padding: '6px 10px', fontSize: t.font.size.sm, color: t.color.text }}>
      <div>🔗 {label}</div>
      <div style={{ color: t.color.textMuted, fontSize: t.font.size.xs }}>{sub}</div>
    </div>
  );
}

function SearchInput({ value, onChange, placeholder, width = 260 }) {
  return (
    <div style={{ position: 'relative', width }}>
      <span style={{ position: 'absolute', left: 10, top: 8 }}>
        <Icon name="search" size={14} color={t.color.textMuted} />
      </span>
      <input
        value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        style={{
          width: '100%', boxSizing: 'border-box', padding: '8px 10px 8px 32px',
          border: `1px solid ${t.color.borderStrong}`, borderRadius: t.radius.sm,
          fontFamily: t.font.family, fontSize: t.font.size.sm,
        }}
      />
    </div>
  );
}

function GQSelect({ value, onChange, options }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)} style={{
      padding: '8px 10px', border: `1px solid ${t.color.borderStrong}`,
      borderRadius: t.radius.sm, background: t.color.bg, color: t.color.text,
      fontFamily: t.font.family, fontSize: t.font.size.sm,
    }}>
      {options.map(o => (
        <option key={o} value={o} style={{ background: t.color.bg, color: t.color.text }}>{o}</option>
      ))}
    </select>
  );
}

// ---------------------------------------------------------------------------
// Screen: Upload Sources
// ---------------------------------------------------------------------------
function UploadSourcesScreen() {
  const [queue,     setQueue]     = useState([]);
  const [hover,     setHover]     = useState(false);
  const [toastMsg,  setToastMsg]  = useState(null);
  const fileRef = useRef(null);

  useEffect(() => { gqSupportService.listQueue().then(setQueue); }, []);

  const onDrop = async (e) => {
    e.preventDefault(); setHover(false);
    const files = [...(e.dataTransfer?.files || [])];
    if (!files.length) return;
    await gqSupportService.uploadFiles(files);
    const added = files.map(f => ({
      id: 'q' + Date.now() + Math.random(),
      name: f.name, progress: 0, status: 'queued',
      note: 'Just uploaded · awaiting indexing',
    }));
    setQueue(prev => [...added, ...prev]);
    setToastMsg(`Uploaded ${files.length} file(s)`);
    setTimeout(() => setToastMsg(null), 2500);
  };

  const onBrowse = () => fileRef.current?.click();
  const onPick   = (e) => onDrop({ preventDefault() {}, dataTransfer: { files: e.target.files } });

  return (
    <div style={{ padding: 24, display: 'flex', gap: 16 }}>
      <div style={{ flex: 1 }}>
        {/* Drop zone */}
        <div
          onDragOver={e => { e.preventDefault(); setHover(true); }}
          onDragLeave={() => setHover(false)}
          onDrop={onDrop}
          onClick={onBrowse}
          style={{
            border: `2px dashed ${t.color.primary}`, borderRadius: t.radius.lg,
            padding: 60, textAlign: 'center',
            background: hover ? t.color.primarySoft : t.color.bgSoft,
            cursor: 'pointer', transition: 'background 0.15s',
          }}
        >
          <div style={{ marginBottom: 8 }}><Icon name="upload" size={48} color={t.color.textMuted} /></div>
          <div style={{ fontSize: t.font.size.xl, fontWeight: 700, color: t.color.primary }}>Drop .md files or a folder here</div>
          <div style={{ color: t.color.textMuted, marginTop: 6 }}>or</div>
          <Btn style={{ marginTop: 12 }} onClick={e => { e.stopPropagation(); onBrowse(); }}>Browse files</Btn>
          <div style={{ fontSize: t.font.size.xs, color: t.color.textMuted, marginTop: 16 }}>
            Accepts: .md · Max 50 MB per file · Multiple files OK
          </div>
          <input ref={fileRef} type="file" multiple accept=".md" onChange={onPick} style={{ display: 'none' }} />
        </div>

        {/* Connectors */}
        <div style={{ marginTop: 28 }}>
          <h3 style={{ margin: '0 0 4px', fontSize: t.font.size.xl, color: t.color.text }}>Or connect a live source</h3>
          <div style={{ color: t.color.textMuted, fontSize: t.font.size.sm, marginBottom: 12 }}>
            The agent keeps its index in sync as the connected repository changes.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
            <ConnectorCard name="GitHub"     sub="Sync a docs/ folder"        connected />
            <ConnectorCard name="GitLab"     sub="Sync wikis or repo paths"            />
            <ConnectorCard name="Confluence" sub="Pull spaces & pages as .md"  connected />
            <ConnectorCard name="SharePoint" sub="Pull library docs as .md"            />
          </div>
        </div>
      </div>

      {/* Queue */}
      <aside style={{
        width: 380, background: t.color.bgSoft, border: `1px solid ${t.color.border}`,
        borderRadius: t.radius.md, padding: 16, alignSelf: 'flex-start',
      }}>
        <div style={{ fontSize: t.font.size.lg, fontWeight: 700 }}>Indexing queue</div>
        <div style={{ color: t.color.textMuted, fontSize: t.font.size.xs, marginBottom: 12 }}>
          Files being chunked, embedded, and indexed.
        </div>
        {queue.map(q => <QueueRow key={q.id} q={q} />)}
        {!queue.length && <div style={{ color: t.color.textMuted, fontSize: t.font.size.sm }}>Queue is empty.</div>}
      </aside>

      {toastMsg && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          background: t.color.primary, color: '#fff',
          padding: '10px 18px', borderRadius: t.radius.md, boxShadow: t.shadow.lg,
        }}>{toastMsg}</div>
      )}
    </div>
  );
}

function ConnectorCard({ name, sub, connected }) {
  return (
    <div style={{ border: `1px solid ${t.color.borderStrong}`, borderRadius: t.radius.md, padding: 14, background: t.color.bg }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{
          width: 30, height: 30, borderRadius: '50%', background: t.color.primary,
          color: '#fff', fontWeight: 700, fontSize: 12,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>{name.slice(0, 2).toUpperCase()}</div>
        <div style={{ fontWeight: 700 }}>{name}</div>
      </div>
      <div style={{ color: t.color.textMuted, fontSize: t.font.size.xs, marginTop: 8, height: 28 }}>{sub}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
        <Btn size="sm">{connected ? 'Manage' : 'Connect'}</Btn>
        {connected && <span style={{ color: t.color.success, fontSize: t.font.size.xs }}>● connected</span>}
      </div>
    </div>
  );
}

function QueueRow({ q }) {
  const barColor = q.status === 'done' ? t.color.success : q.status === 'failed' ? t.color.danger : t.color.warning;
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: t.font.size.sm, fontWeight: 600 }}>
        <span><Icon name="file" size={13} color={t.color.textMuted} /> &nbsp; {q.name}</span>
        <span style={{ color: barColor }}>
          {q.status === 'done' ? '100%' : q.status === 'queued' ? 'queued' : q.status === 'failed' ? 'failed' : q.progress + '%'}
        </span>
      </div>
      <div style={{ height: 6, background: t.color.border, borderRadius: 3, marginTop: 6, overflow: 'hidden' }}>
        <div style={{ width: q.progress + '%', height: '100%', background: barColor, transition: 'width 0.3s' }} />
      </div>
      <div style={{ color: q.status === 'failed' ? t.color.dangerText : t.color.textMuted, fontSize: t.font.size.xs, marginTop: 4 }}>
        {q.note} {q.status === 'failed' && <span style={{ color: t.color.primary, cursor: 'pointer', marginLeft: 6 }}>Retry ⟳</span>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Screen: Analytics
// ---------------------------------------------------------------------------
const ANALYTICS_RANGE_DAYS = {
  'Last 7 days': 7,
  'Last 30 days': 30,
  'Last 90 days': 90,
  'Year to date': Math.max(1, Math.ceil((Date.now() - new Date(new Date().getFullYear(), 0, 1).getTime()) / 86400000)),
};

function AnalyticsScreen({ onReview }) {
  const [data, setData] = useState(null);
  const [range, setRange] = useState('Last 30 days');
  useEffect(() => {
    setData(null);   // show Loading while the new range loads
    gqSupportService.getAnalytics(ANALYTICS_RANGE_DAYS[range] ?? 30).then(setData);
  }, [range]);
  if (!data) return <Loading />;

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <GQSelect value={range} onChange={setRange} options={['Last 7 days', 'Last 30 days', 'Last 90 days', 'Year to date']} />
      </div>

      {/* KPI cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
        <KpiCard label="QUERIES"                value={data.kpis.queries.value.toLocaleString()} delta={data.kpis.queries.delta}        direction="up"  />
        <KpiCard label="AVG RESPONSE TIME"      value={data.kpis.avgResponseSec.value + 's'}    delta={data.kpis.avgResponseSec.delta}  direction="up"  />
        <KpiCard label="UNANSWERED / LOW CONF"  value={data.kpis.unanswered.value}              delta={data.kpis.unanswered.delta}      direction="bad" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12, marginTop: 16 }}>
        <div style={cardSt}>
          <div style={{ fontSize: t.font.size.lg, fontWeight: 700 }}>Queries per day</div>
          <div style={{ fontSize: t.font.size.xs, color: t.color.textMuted, marginBottom: 12 }}>
            Total answered queries per day
          </div>
          <SimpleChart series={data.series} />
        </div>
        <div style={cardSt}>
          <div style={{ fontSize: t.font.size.lg, fontWeight: 700, marginBottom: 10 }}>Top sources cited</div>
          {(() => {
            const max = Math.max(1, ...data.topSources.map(s => s.count));
            return data.topSources.map(s => (
              <div key={s.name} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <div style={{ width: 180, fontSize: t.font.size.sm, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{s.name}</div>
                <div style={{ flex: 1, height: 14, background: t.color.bgHover, borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{ width: (s.count / max * 100) + '%', height: '100%', background: t.color.primary }} />
                </div>
                <div style={{ width: 32, textAlign: 'right', fontSize: t.font.size.sm }}>{s.count}</div>
              </div>
            ));
          })()}
        </div>
      </div>

      <div style={{ ...cardSt, marginTop: 16 }}>
        <div style={{ fontSize: t.font.size.lg, fontWeight: 700, marginBottom: 6 }}>
          Flagged answers — needs review{' '}
          <span style={{ color: t.color.textMuted, fontWeight: 400 }}>({data.flagged.length})</span>
        </div>
        {data.flagged.map(f => (
          <div key={f.id} style={{
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '10px 4px', borderTop: `1px solid ${t.color.border}`,
            fontSize: t.font.size.sm,
          }}>
            <span style={{ color: t.color.danger, fontWeight: 700 }}>▼</span>
            <div style={{ flex: 1 }}>{f.q}</div>
            <div style={{ color: t.color.textMuted }}>{f.reason}</div>
            <span
              onClick={() => f.convId && onReview?.(f.convId)}
              title={f.convId ? 'Open this conversation in History' : 'No conversation linked'}
              style={{ color: t.color.primary, cursor: f.convId ? 'pointer' : 'not-allowed', opacity: f.convId ? 1 : 0.5 }}>
              Review →
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

const cardSt = {
  background: t.color.bg, border: `1px solid ${t.color.border}`,
  borderRadius: t.radius.md, padding: 16,
};

function KpiCard({ label, value, delta, direction }) {
  const color = direction === 'bad' ? t.color.danger : t.color.success;
  const arrow = direction === 'bad' ? '▼' : '▲';
  return (
    <div style={cardSt}>
      <div style={{ fontSize: t.font.size.xs, fontWeight: 700, color: t.color.textMuted, letterSpacing: 0.5 }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginTop: 6 }}>
        <div style={{ fontSize: 30, fontWeight: 700 }}>{value}</div>
        <div style={{ color, fontSize: t.font.size.sm }}>{arrow} {delta}</div>
      </div>
    </div>
  );
}

function SimpleChart({ series }) {
  const W = 620, H = 200, PAD = 24;
  const [hover, setHover] = useState(null);   // hovered day index

  // No data yet (e.g. a user with no answered queries in range). Render a
  // placeholder instead of indexing series[0] (which crashes on an empty array).
  if (!series || series.length === 0) {
    return (
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: 'block' }}>
        <line x1={PAD} y1={H - PAD} x2={W - PAD} y2={H - PAD} stroke={t.color.border} />
        <line x1={PAD} y1={PAD}     x2={PAD}      y2={H - PAD} stroke={t.color.border} />
        <text x={W / 2} y={H / 2} fontSize="12" fill={t.color.textMuted} textAnchor="middle">
          No data for this period yet
        </text>
      </svg>
    );
  }

  const max = Math.max(1, ...series.map(p => p.total));
  const xStep = (W - PAD * 2) / Math.max(1, series.length - 1);
  const xAt = i => PAD + i * xStep;
  const yVal = v => H - PAD - (v / max) * (H - PAD * 2);
  const lineFor = (key) =>
    series.map((p, i) => `${i === 0 ? 'M' : 'L'} ${xAt(i)} ${yVal(p[key])}`).join(' ');
  const colW = (W - PAD * 2) / Math.max(1, series.length);
  const hp = hover != null ? series[hover] : null;

  // Tooltip box geometry — flip to the left of the cursor near the right edge.
  const tw = 104, th = 34;
  let tx = hp ? xAt(hover) + 8 : 0;
  if (hp && tx + tw > W - 2) tx = xAt(hover) - tw - 8;
  const ty = PAD + 2;

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: 'block' }}>
      <line x1={PAD} y1={H - PAD} x2={W - PAD} y2={H - PAD} stroke={t.color.border} />
      <line x1={PAD} y1={PAD}     x2={PAD}      y2={H - PAD} stroke={t.color.border} />
      <path d={lineFor('total')} fill="none" stroke={t.color.primary} strokeWidth="2" />
      <text x={PAD}      y={H - 6} fontSize="10" fill={t.color.textMuted}>{series[0].d}</text>
      <text x={W / 2}    y={H - 6} fontSize="10" fill={t.color.textMuted} textAnchor="middle">{series[Math.floor(series.length / 2)].d}</text>
      <text x={W - PAD}  y={H - 6} fontSize="10" fill={t.color.textMuted} textAnchor="end">{series[series.length - 1].d}</text>

      {/* Hover guide line, point markers, and a tooltip showing each count */}
      {hp && (
        <g pointerEvents="none">
          <line x1={xAt(hover)} y1={PAD} x2={xAt(hover)} y2={H - PAD} stroke={t.color.borderStrong} strokeDasharray="3 3" />
          <circle cx={xAt(hover)} cy={yVal(hp.total)} r="3.5" fill={t.color.primary} />
          <rect x={tx} y={ty} width={tw} height={th} rx="4" fill={t.color.bg} stroke={t.color.border} />
          <text x={tx + 8} y={ty + 15} fontSize="10" fontWeight="700" fill={t.color.text}>{hp.d}</text>
          <text x={tx + 8} y={ty + 30} fontSize="10" fill={t.color.primary}>Total: {hp.total}</text>
        </g>
      )}

      {/* Invisible per-day hit areas so hovering anywhere in a column works */}
      {series.map((p, i) => (
        <rect
          key={i} x={xAt(i) - colW / 2} y={PAD} width={colW} height={H - PAD * 2}
          fill="transparent"
          onMouseEnter={() => setHover(i)}
          onMouseLeave={() => setHover(null)}
        />
      ))}
    </svg>
  );
}

function Loading() {
  return <div style={{ padding: 60, textAlign: 'center', color: t.color.textMuted }}>Loading…</div>;
}

// Full-screen blocking loader — same branded hex spinner the main app shows during
// operations (uses the shared .spinner-* CSS classes from App.css).
function OverlayLoader({ show }) {
  if (!show) return null;
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
      zIndex: 999999, backgroundColor: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div className="spinner-container">
        <div className="spinner-socket">
          <div className="spinner-hex spinner-tile-1"><div className="spinner-brick rotate-0"></div><div className="spinner-brick rotate-1"></div><div className="spinner-brick rotate-2"></div></div>
          <div className="spinner-hex spinner-tile-2"><div className="spinner-brick rotate-0"></div><div className="spinner-brick rotate-1"></div><div className="spinner-brick rotate-2"></div></div>
          <div className="spinner-hex spinner-tile-3 spinner-row-2"><div className="spinner-brick rotate-0"></div><div className="spinner-brick rotate-1"></div><div className="spinner-brick rotate-2"></div></div>
          <div className="spinner-hex spinner-tile-4 spinner-row-2"><div className="spinner-brick rotate-0"></div><div className="spinner-brick rotate-1"></div><div className="spinner-brick rotate-2"></div></div>
          <div className="spinner-hex spinner-tile-5 spinner-row-2"><div className="spinner-brick rotate-0"></div><div className="spinner-brick rotate-1"></div><div className="spinner-brick rotate-2"></div></div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Screen: Settings
// ---------------------------------------------------------------------------
const SETTINGS_TABS = [
  { id: 'persona',    label: 'Agent persona' },
  { id: 'knowledge',  label: 'Knowledge & retrieval' },
  { id: 'escalation', label: 'Escalation' },
  { id: 'access',     label: 'Access & roles' },
  { id: 'branding',   label: 'Branding' },
  { id: 'api',        label: 'API & embeds' },
  { id: 'audit',      label: 'Audit log' },
];

function SettingsScreen() {
  const [settingsTab, setSettingsTab] = useState('persona');
  const [settings,    setSettings]    = useState(null);
  const [saved,       setSaved]       = useState(false);
  const [saving,      setSaving]      = useState(false);

  useEffect(() => { gqSupportService.getSettings().then(setSettings); }, []);
  if (!settings) return <Loading />;

  const setField = (k, v) => { setSettings(prev => ({ ...prev, [k]: v })); setSaved(false); };

  const onSave = async () => {
    setSaving(true);
    try {
      await gqSupportService.saveSettings(settings);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'flex', gap: 16, padding: 24 }}>
      <OverlayLoader show={saving} />
      <aside style={{ width: 240, background: t.color.bgSoft, border: `1px solid ${t.color.border}`, borderRadius: t.radius.md, padding: 8, alignSelf: 'flex-start' }}>
        {SETTINGS_TABS.map(st => {
          const on = settingsTab === st.id;
          return (
            <div key={st.id} onClick={() => setSettingsTab(st.id)} style={{
              padding: '10px 12px', borderRadius: t.radius.sm, cursor: 'pointer',
              background: on ? t.color.bgHover : 'transparent',
              fontWeight: on ? 700 : 400, fontSize: t.font.size.sm,
            }}>{st.label}</div>
          );
        })}
      </aside>

      <main style={{ flex: 1, background: t.color.bg, border: `1px solid ${t.color.border}`, borderRadius: t.radius.md, padding: 24 }}>
        {settingsTab === 'persona' && (
          <>
            <h2 style={{ margin: 0, fontSize: 22 }}>Agent persona</h2>
            <p style={{ color: t.color.textMuted, fontSize: t.font.size.sm, marginTop: 4 }}>
              How the GQ Support Agent introduces itself and how it writes.
            </p>

            <SettingsField label="Agent name">
              <input value={settings.agentName} onChange={e => setField('agentName', e.target.value)} style={inputSt} />
            </SettingsField>

            <SettingsField label="First-message greeting">
              <textarea value={settings.greeting} onChange={e => setField('greeting', e.target.value)}
                rows={3} style={{ ...inputSt, fontFamily: t.font.family, resize: 'vertical' }} />
            </SettingsField>

            <SettingsField label="System instructions">
              <textarea value={settings.systemPrompt} onChange={e => setField('systemPrompt', e.target.value)}
                rows={9} style={{ ...inputSt, fontFamily: t.font.mono, fontSize: t.font.size.sm, resize: 'vertical' }} />
            </SettingsField>

            <SettingsField label="Response style">
              <div style={{ display: 'flex', gap: 8 }}>
                {['Concise', 'Detailed', 'Step-by-step'].map(s => {
                  const on = settings.style === s;
                  return (
                    <button key={s} onClick={() => setField('style', s)} style={{
                      padding: '8px 16px', borderRadius: t.radius.sm,
                      border: `1px solid ${on ? t.color.primary : t.color.borderStrong}`,
                      background: on ? t.color.primary : t.color.bg,
                      color: on ? '#fff' : t.color.text,
                      fontFamily: t.font.family, fontSize: t.font.size.sm,
                      fontWeight: on ? 700 : 400, cursor: 'pointer',
                    }}>{s}</button>
                  );
                })}
              </div>
            </SettingsField>

            <SettingsField label={`Confidence threshold for "I don't know" — ${settings.confidenceThreshold}%`}>
              <input type="range" min={0} max={100} step={5}
                value={settings.confidenceThreshold}
                onChange={e => setField('confidenceThreshold', +e.target.value)}
                style={{ width: 400 }} />
              <div style={{ fontSize: t.font.size.xs, color: t.color.textMuted, marginTop: 4 }}>
                Below this confidence the agent says "I'm not certain — escalate to L2" instead of guessing.
              </div>
            </SettingsField>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 24, borderTop: `1px solid ${t.color.border}`, paddingTop: 14 }}>
              {saved && <span style={{ color: t.color.success, fontSize: t.font.size.sm, alignSelf: 'center' }}>✓ Saved</span>}
              <Btn variant="secondary">Cancel</Btn>
              <Btn onClick={onSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Btn>
            </div>
          </>
        )}
        {settingsTab !== 'persona' && (
          <div style={{ padding: 60, textAlign: 'center', color: t.color.textMuted }}>
            <h3 style={{ marginTop: 0, color: t.color.text }}>{SETTINGS_TABS.find(s => s.id === settingsTab)?.label}</h3>
            <p>This sub-panel follows the same form pattern as Agent persona.</p>
          </div>
        )}
      </main>
    </div>
  );
}

const inputSt = {
  width: '100%', boxSizing: 'border-box', padding: '8px 10px',
  border: `1px solid ${t.color.borderStrong}`, borderRadius: t.radius.sm,
  fontFamily: t.font.family, fontSize: t.font.size.md,
  color: t.color.text, background: t.color.bg,
};

function SettingsField({ label, children }) {
  return (
    <div style={{ marginTop: 18 }}>
      <div style={{ fontSize: t.font.size.sm, fontWeight: 600, color: t.color.text, marginBottom: 6 }}>{label}</div>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Q&A Library — curate submitted answers into verified-qa.md, publish to AIS.
// Self-contained (unique QA*/qa* names) so it can't collide with anything else.
// ---------------------------------------------------------------------------

const qaInputStyle = {
  width: '100%', boxSizing: 'border-box', padding: '8px 10px',
  border: `1px solid ${t.color.borderStrong}`, borderRadius: t.radius.sm,
  fontFamily: t.font.family, fontSize: t.font.size.md,
  color: t.color.text, background: t.color.bg,
};
const qaChipStyle = {
  display: 'inline-block', padding: '2px 8px', borderRadius: t.radius.pill,
  background: t.color.bgHover, color: t.color.primaryDark,
  fontSize: t.font.size.xs, marginRight: 4,
};
// Only show citation chips that point to a real source file — drop the
// placeholder "unknown" / empty ones the agent sometimes returns.
const realCitations = (cits) =>
  (cits || []).filter(c => c && c.file && String(c.file).trim() && String(c.file).trim().toLowerCase() !== 'unknown');
const qaTagStyle = {
  display: 'inline-block', padding: '2px 8px', borderRadius: t.radius.pill,
  background: t.color.warningBg, color: t.color.warningText,
  fontSize: t.font.size.xs, marginRight: 4,
};

const QALabel = ({ children }) => (
  <div style={{ fontSize: t.font.size.xs, fontWeight: 700, color: t.color.textMuted, marginBottom: 4, letterSpacing: 0.4 }}>
    {children}
  </div>
);

function QASectionTab({ label, on, onClick }) {
  return (
    <button onClick={onClick} style={{
      background: 'none', border: 'none', cursor: 'pointer',
      padding: '8px 14px', fontFamily: t.font.family,
      fontSize: t.font.size.md, fontWeight: on ? 700 : 400,
      color: on ? t.color.primary : t.color.textMuted,
      borderBottom: on ? `2px solid ${t.color.primary}` : '2px solid transparent',
      marginBottom: -1,
    }}>{label}</button>
  );
}

function QAEmptyState({ label }) {
  return (
    <div style={{
      padding: 32, textAlign: 'center', color: t.color.textMuted,
      background: t.color.bgSoft, border: `1px dashed ${t.color.border}`,
      borderRadius: t.radius.md,
    }}>{label}</div>
  );
}

function QASuggestionCard({ s, editing, onStartEdit, onCancelEdit, onApprove, onDismiss, busy }) {
  const [resolvedQ, setResolvedQ] = useState(() => resolveSuggestionQuestion(s));
  const [q, setQ] = useState(resolvedQ);
  const [a, setA] = useState(cleanSuggestionAnswer(s.answer));

  useEffect(() => {
    const local = resolveSuggestionQuestion(s);
    setResolvedQ(local);
    setQ(local);
    setA(cleanSuggestionAnswer(s.answer));
  }, [s.id, s.question, s.answer]);
  return (
    <div style={{
      border: `1px solid ${t.color.border}`, borderLeft: `4px solid ${t.color.success}`,
      borderRadius: t.radius.md, padding: 14, background: t.color.bg,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <Icon name="file" size={16} color={t.color.success} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: t.font.size.xs, color: t.color.textMuted, marginBottom: 6 }}>
            Submitted by <strong>{s.submittedBy}</strong>{s.submittedAt ? ` · ${new Date(s.submittedAt).toLocaleString()}` : ''}
          </div>
          {editing ? (
            <>
              <QALabel>Question</QALabel>
              <textarea value={q} onChange={e => setQ(e.target.value)} rows={2} style={{ ...qaInputStyle, marginBottom: 8 }} />
              <QALabel>Answer</QALabel>
              <textarea value={a} onChange={e => setA(e.target.value)} rows={6} style={{ ...qaInputStyle, marginBottom: 8 }} />
            </>
          ) : (
            <>
              <div style={{ fontWeight: 700, fontSize: t.font.size.md, marginBottom: 6 }}>
                {renderBold(resolvedQ, 'sq')}
              </div>
              <div style={{ color: t.color.text, fontSize: t.font.size.md, marginBottom: 8 }}>
                {renderRich(cleanSuggestionAnswer(s.answer))}
              </div>
            </>
          )}
          {realCitations(s.citations).length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 10 }}>
              {realCitations(s.citations).map(c => <span key={c.n} style={qaChipStyle}>[{c.n}] {c.file}</span>)}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            {editing ? (
              <>
                <Btn variant="secondary" onClick={onCancelEdit} disabled={busy}>Cancel</Btn>
                <Btn onClick={() => onApprove({ question: q, answer: a, citations: s.citations || [], tags: [] })} disabled={busy}>Save &amp; Approve</Btn>
              </>
            ) : (
              <>
                <Btn variant="ghost" onClick={onDismiss} disabled={busy}>Dismiss</Btn>
                <Btn variant="secondary" onClick={onStartEdit} disabled={busy}>Edit</Btn>
                <Btn onClick={() => onApprove({ question: resolvedQ, answer: s.answer, citations: s.citations || [], tags: [] })} disabled={busy}>Approve as-is</Btn>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function QAEntryCard({ e, editing, onStartEdit, onCancelEdit, onSave, onDelete, busy }) {
  const displayQuestion = resolveSuggestionQuestion(e);
  const [a, setA]       = useState(cleanSuggestionAnswer(e.answer));
  useEffect(() => {
    setA(cleanSuggestionAnswer(e.answer));
  }, [e.id, e.question, e.answer]);
  return (
    <div style={{ border: `1px solid ${t.color.border}`, borderRadius: t.radius.md, padding: 14, background: t.color.bg }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <Icon name="file" size={14} color={t.color.primary} />
        <div style={{ fontWeight: 700, fontSize: t.font.size.md, flex: 1 }}>{renderBold(displayQuestion, 'eq')}</div>
        <Badge tone="success">Approved</Badge>
        <span style={{ color: t.color.textMuted, fontSize: t.font.size.xs }}>
          {(e.approvedAt || '').slice(0, 10)}{e.approvedBy ? ` · ${e.approvedBy}` : ''}
        </span>
      </div>
      {editing ? (
        <>
          <QALabel>Answer</QALabel>
          <textarea value={a} onChange={ev => setA(ev.target.value)} rows={6} style={{ ...qaInputStyle, marginBottom: 10 }} />
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Btn variant="secondary" onClick={onCancelEdit} disabled={busy}>Cancel</Btn>
            <Btn onClick={() => onSave({ question: displayQuestion, answer: a, citations: e.citations || [], tags: e.tags || [] })} disabled={busy}>Save</Btn>
          </div>
        </>
      ) : (
        <>
          <div style={{ color: t.color.text, fontSize: t.font.size.md }}>{renderRich(cleanSuggestionAnswer(e.answer))}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 8 }}>
            {realCitations(e.citations).map(c => <span key={c.n} style={qaChipStyle}>[{c.n}] {c.file}</span>)}
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
            <Btn variant="ghost" onClick={onDelete} disabled={busy} style={{ color: t.color.danger }}>Delete</Btn>
            <Btn variant="secondary" onClick={onStartEdit} disabled={busy}>Edit</Btn>
          </div>
        </>
      )}
    </div>
  );
}

function QALibraryScreen() {
  const [section, setSection]         = useState('suggestions');
  const [suggestions, setSuggestions] = useState([]);
  const [entries, setEntries]         = useState([]);
  const [editing, setEditing]         = useState(null);
  const [busy, setBusy]               = useState(false);
  const [toast, setToast]             = useState(null);
  const [pendingDelete,  setPendingDelete]  = useState(null);   // entry awaiting delete confirm
  const [pendingDismiss, setPendingDismiss] = useState(null);   // suggestion awaiting dismiss confirm

  const refresh = async () => {
    setSuggestions(await gqSupportService.listQASuggestions());
    setEntries(await gqSupportService.listQAEntries());
  };
  useEffect(() => { refresh(); }, []);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 2500); };

  const onApprove = async (sug, edits) => {
    setBusy(true);
    try { await gqSupportService.promoteSuggestion(sug.id, edits); await refresh(); notifySuggestionsChanged(); setEditing(null); showToast('Added to Q&A Library'); }
    catch (err) { showToast(`Approve failed: ${err.message}`); }
    finally { setBusy(false); }
  };
  const onDismiss = (sug) => {
    setPendingDismiss(sug);   // open the styled confirm popup
  };
  const confirmDismiss = async () => {
    const sug = pendingDismiss;
    setPendingDismiss(null);
    if (!sug) return;
    try {
      await gqSupportService.dismissSuggestion(sug.id);
      setSuggestions(prev => prev.filter(s => s.id !== sug.id));  // optimistic
      notifySuggestionsChanged();
      showToast('Suggestion dismissed.');
    } catch (err) {
      await refresh();   // restore on failure
      showToast(`Dismiss failed: ${err.message}`);
    }
  };
  const onSaveEdit = async (entry, edits) => {
    setBusy(true);
    try { await gqSupportService.updateQAEntry(entry.id, edits); await refresh(); setEditing(null); showToast('Entry updated'); }
    catch (err) { showToast(`Update failed: ${err.message}`); }
    finally { setBusy(false); }
  };
  const onDelete = (entry) => {
    setPendingDelete(entry);   // open the styled confirm popup
  };
  const confirmDelete = async () => {
    const entry = pendingDelete;
    setPendingDelete(null);
    if (!entry) return;
    setBusy(true);   // loader until the delete + refresh finish
    try {
      await gqSupportService.deleteQAEntry(entry.id);
      await refresh();
      showToast('Entry deleted from library and AIS.');
    } catch (err) {
      showToast(`Delete failed: ${err.message}`);
    } finally {
      setBusy(false);
    }
  };
  const onDownload = async () => {
    const md = await gqSupportService.exportQAEntries();
    const blob = new Blob([md], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'verified-qa.md'; a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div style={{ padding: 24 }}>
      <OverlayLoader show={busy} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 20 }}>Q&amp;A Library</h2>
        <span style={{ color: t.color.textMuted, fontSize: t.font.size.sm }}>
          Answers submitted for approval land in Suggestions; approving one indexes it to AIS automatically.
        </span>
        <div style={{ flex: 1 }} />
        <Btn variant="secondary" onClick={onDownload}><Icon name="file" size={13} /> &nbsp; Download verified-qa.md</Btn>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 14, borderBottom: `1px solid ${t.color.border}` }}>
        <QASectionTab label={`Suggestions (${suggestions.length})`} on={section === 'suggestions'} onClick={() => setSection('suggestions')} />
        <QASectionTab label={`Approved (${entries.length})`} on={section === 'approved'} onClick={() => setSection('approved')} />
      </div>

      {section === 'suggestions' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {!suggestions.length && <QAEmptyState label='No suggestions waiting — when a rep clicks "Send for approval" in Ask, it lands here.' />}
          {suggestions.map(s => (
            <QASuggestionCard key={s.id} s={s}
              editing={editing?.id === s.id}
              onStartEdit={() => setEditing(s)} onCancelEdit={() => setEditing(null)}
              onApprove={(edits) => onApprove(s, edits)} onDismiss={() => onDismiss(s)} busy={busy} />
          ))}
        </div>
      )}

      {section === 'approved' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {!entries.length && <QAEmptyState label="No approved entries yet — approve a suggestion to start the library." />}
          {entries.map(e => (
            <QAEntryCard key={e.id} e={e}
              editing={editing?.id === e.id}
              onStartEdit={() => setEditing(e)} onCancelEdit={() => setEditing(null)}
              onSave={(edits) => onSaveEdit(e, edits)} onDelete={() => onDelete(e)} busy={busy} />
          ))}
        </div>
      )}

      {toast && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          background: t.color.success, color: '#fff',
          padding: '10px 18px', borderRadius: t.radius.md, boxShadow: t.shadow.lg, zIndex: 1000,
        }}>{toast}</div>
      )}

      <ConfirmModal
        show={!!pendingDelete}
        title="Delete Q&A entry"
        message="Are you sure you want to delete this Q&A entry? It will be removed from the Q&A Library and the AIS knowledge store."
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />

      <ConfirmModal
        show={!!pendingDismiss}
        title="Dismiss suggestion"
        message="Are you sure you want to dismiss this suggestion? It will not be added to the Q&A Library."
        onConfirm={confirmDismiss}
        onCancel={() => setPendingDismiss(null)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab registry
// ---------------------------------------------------------------------------
function EscalationScreen() {
  const [items, setItems]   = useState([]);
  const [filter, setFilter] = useState('open');   // open | resolved | all
  const [loading, setLoading] = useState(true);
  const [busy, setBusy]     = useState(false);
  const [toast, setToast]   = useState(null);

  const refresh = async () => {
    setLoading(true);
    try { setItems(await gqSupportService.listEscalations()); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 2500); };

  const setStatus = async (id, status) => {
    setBusy(true);
    try {
      await gqSupportService.updateEscalation(id, status);
      await refresh();
      showToast(status === 'resolved' ? 'Marked resolved' : 'Reopened');
    } finally { setBusy(false); }
  };

  const priorityTone = { urgent: 'danger', high: 'warning', normal: 'info', low: 'neutral' };
  const visible = items.filter(e => filter === 'all' ? true : e.status === filter);

  return (
    <div style={{ padding: 24 }}>
      <OverlayLoader show={busy} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 20 }}>Escalation queue</h2>
        <span style={{ color: t.color.textMuted, fontSize: t.font.size.sm }}>
          Answers escalated to L2. The GQ support team is emailed on submit.
        </span>
        <div style={{ flex: 1 }} />
        <select
          value={filter}
          onChange={e => setFilter(e.target.value)}
          style={{
            padding: '7px 10px', border: `1px solid ${t.color.borderStrong}`,
            borderRadius: t.radius.sm, fontFamily: t.font.family, fontSize: t.font.size.sm,
            background: t.color.bg, color: t.color.text,
          }}>
          <option value="open"     style={{ background: t.color.bg, color: t.color.text }}>Open</option>
          <option value="resolved" style={{ background: t.color.bg, color: t.color.text }}>Resolved</option>
          <option value="all"      style={{ background: t.color.bg, color: t.color.text }}>All</option>
        </select>
        <Btn variant="secondary" onClick={refresh}>Refresh</Btn>
      </div>

      {loading ? <Loading /> : visible.length === 0 ? (
        <div style={{ color: t.color.textMuted, padding: '40px 0', textAlign: 'center' }}>
          No {filter === 'all' ? '' : filter} escalations.
        </div>
      ) : visible.map(e => (
        <div key={e.id} style={{
          border: `1px solid ${t.color.border}`, borderRadius: t.radius.md,
          padding: 16, marginBottom: 12, background: t.color.bg,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
            <Badge tone={priorityTone[e.priority] || 'neutral'}>{(e.priority || 'normal').toUpperCase()}</Badge>
            <Badge tone={e.status === 'resolved' ? 'success' : 'neutral'}>{e.status}</Badge>
            {e.emailSent === false && <Badge tone="danger">email not sent</Badge>}
            <span style={{ color: t.color.textMuted, fontSize: t.font.size.xs }}>
              {e.createdBy} · {new Date(e.createdAt).toLocaleString()}
            </span>
            <div style={{ flex: 1 }} />
            {e.status === 'open'
              ? <Btn size="sm" onClick={() => setStatus(e.id, 'resolved')}>Mark resolved</Btn>
              : <Btn size="sm" variant="secondary" onClick={() => setStatus(e.id, 'open')}>Reopen</Btn>}
          </div>
          <div style={{ fontWeight: 600, fontSize: t.font.size.md, color: t.color.text, marginBottom: 4 }}>{e.question}</div>
          <div style={{ color: t.color.text, fontSize: t.font.size.md, marginBottom: 8 }}>{renderRich(e.answer)}</div>
          {e.note && (
            <div style={{ background: t.color.bgSoft, borderRadius: t.radius.sm, padding: '8px 10px', fontSize: t.font.size.sm }}>
              <strong>Note:</strong> {e.note}
            </div>
          )}
          {realCitations(e.citations).length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
              {realCitations(e.citations).map(c => (
                <span key={c.n} style={{
                  border: `1px solid ${t.color.border}`, borderRadius: t.radius.pill,
                  padding: '2px 8px', fontSize: t.font.size.xs, color: t.color.textMuted,
                }}>[{c.n}] {c.file}</span>
              ))}
            </div>
          )}
        </div>
      ))}

      {toast && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          background: t.color.text, color: '#fff', padding: '10px 18px',
          borderRadius: t.radius.sm, fontSize: t.font.size.sm, zIndex: 2000,
        }}>{toast}</div>
      )}
    </div>
  );
}


const TABS = [
  { id: 'ask',        label: 'Ask',            component: AskScreen            },
  { id: 'history',    label: 'History',        component: AskScreen            },
  { id: 'qa',         label: 'Q&A Library',    component: QALibraryScreen,      adminOnly: true },
  // { id: 'escalation', label: 'Escalation', component: EscalationScreen, adminOnly: true },  // disabled for now
  { id: 'analytics',  label: 'Analytics',      component: AnalyticsScreen,      adminOnly: true },
];

// ---------------------------------------------------------------------------
// Root page component — used by the GQ router at /gqsupport
// ---------------------------------------------------------------------------
export default function GQSupport() {
  const userInfo   = useSelector(state => state.setUserInfo);
  const [accountInfo, setAccountInfo] = useState(null);
  const [ready,       setReady]       = useState(false);   // identity wired up?
  const [activeTab,   setActiveTab]   = useState('ask');
  const [openConvId,  setOpenConvId]  = useState(null);    // deep-link a conversation into History

  // From Analytics "Review →": jump to History with the flagged conversation open.
  const reviewConversation = (convId) => { setOpenConvId(convId); setActiveTab('history'); };

  // Load account info (same pattern as header.js) to get is_admin_class
  // Also wires up the service with the user's identity for API auth headers
  useEffect(() => {
    AccountService.getAccountInfo().then(res => {
      if (res?.response_content) {
        const info = res.response_content;
        setAccountInfo(info);
        configureGQSupport(info.email_address || info.login_name, info.is_admin_class);
      }
    }).catch(() => {}).finally(() => setReady(true));
  }, []);

  const isAdmin = !!(accountInfo?.is_admin_class);
  const Active  = TABS.find(tab => tab.id === activeTab)?.component || AskScreen;

  // Wait until the user identity is set before mounting any screen — otherwise
  // the first listConversations() fetch runs as 'anonymous' and the user's
  // saved history (scoped by X-User-Id on the backend) shows up empty.
  if (!ready) {
    return (
      <div style={{ fontFamily: t.font.family, color: t.color.textMuted, padding: 40, textAlign: 'center' }}>
        Loading GQ Support…
      </div>
    );
  }

  return (
    <div style={{ fontFamily: t.font.family, color: t.color.text }}>
      {/* Sub-tab navigation */}
      <SubTabs tabs={TABS} active={activeTab} onChange={setActiveTab} isAdmin={isAdmin} liveMode={!!process.env.REACT_APP_GQ_SUPPORT_API} />

      {/* Ask: latest 10 + can create/chat. History: all conversations, read-only. */}
      <Active
        conversationLimit={activeTab === 'ask' ? 10 : null}
        readOnly={activeTab === 'history'}
        openConvId={activeTab === 'history' ? openConvId : null}
        onConvOpened={() => setOpenConvId(null)}
        onReview={reviewConversation}
      />
    </div>
  );
}
